import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useWallet } from '../hooks/useWallet';
import { useNativeWallet } from '../hooks/useNativeWallet';
import WalletModal from './WalletModal';

const AmitisLogo = () => (
  <svg width="30" height="30" viewBox="0 0 320 320" xmlns="http://www.w3.org/2000/svg">
    <circle cx="160" cy="160" r="155" fill="#080810"/>
    <circle cx="160" cy="160" r="155" fill="none" stroke="white" strokeWidth="6"/>
    <text x="160" y="252" textAnchor="middle" fontFamily="Arial Black,Arial,sans-serif" fontSize="260" fontWeight="900" fill="white">A</text>
    <path d="M248,72 C268,52 288,68 268,88 C258,98 238,92 248,72Z" fill="#4ade80" opacity="0.7"/>
    <line x1="248" y1="72" x2="268" y2="88" stroke="#4ade80" strokeWidth="1.5" opacity="0.5"/>
  </svg>
);

const navGroups = [
  { label: 'Wallet', items: [
    { to: '/',        icon: '◈', label: 'Dashboard',  exact: true },
    { to: '/rwa',     icon: '⬡', label: 'RWA Assets', badge: '1' },
  ]},
  { label: 'Trading', items: [
    { to: '/swap',   icon: '⇌', label: 'Swap' },
    { to: '/faucet', icon: '◎', label: 'Faucet' },
    { to: '/bridge', icon: '⬟', label: 'Bridge' },
  ]},
  { label: 'Network', items: [
    { to: '/oracle',     icon: '✦', label: 'Oracle Status' },
    { to: '/governance', icon: '⊞', label: 'Governance', badge: '2' },
    { to: '/validators', icon: '❋', label: 'Validators' },
    { to: '/explorer',   icon: '⬛', label: 'Explorer' },
  ]},
];

export default function Sidebar() {
  const ctx          = useWallet();
  const nativeWallet = useNativeWallet();
  const [modalOpen, setModalOpen] = useState(false);

  // Register native wallet address into shared context when it connects
  useEffect(() => {
    if (nativeWallet.address) {
      nativeWallet.fetchBalance(nativeWallet.address).then(bal => {
        ctx.registerNative(nativeWallet.address, bal, nativeWallet);
      });
    } else {
      ctx.clearNative();
    }
  }, [nativeWallet.address]);

  const { address, balance, walletType, blockHeight, shortAddr } = ctx;

  const fmtBal = (balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  const usdVal = '$' + ((balance || 0) * 7.50).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <>
      <aside className="sidebar">
        <NavLink to="/" className="sidebar-brand">
          <AmitisLogo />
          <span className="brand-text">AMITIS</span>
        </NavLink>

        <div className="sidebar-wallet" onClick={() => setModalOpen(true)}>
          <div className="wallet-status-row">
            <div className={`wallet-dot ${address ? '' : 'off'}`}></div>
            <div className="wallet-status-label">
              {walletType === 'native' ? '✦ Native Wallet'
                : walletType === 'keplr' ? '🔑 Keplr'
                : 'Not Connected'}
            </div>
          </div>
          {address ? (
            <>
              <div className="wallet-addr-text">{shortAddr}</div>
              <div className="wallet-bal-main">{fmtBal} AMTS</div>
              <div className="wallet-bal-sub">{usdVal}</div>
            </>
          ) : (
            <>
              <div className="wallet-addr-text" style={{ color: 'rgba(255,255,255,0.3)' }}>
                Click to connect
              </div>
              <div className="wallet-bal-main" style={{ color: 'var(--dimmer)', fontSize: 22 }}>—</div>
              <div className="wallet-bal-sub">Create · Import · Keplr</div>
            </>
          )}
        </div>

        {navGroups.map(group => (
          <React.Fragment key={group.label}>
            <span className="sidebar-section">{group.label}</span>
            {group.items.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}
              >
                <span className="nav-icon">{item.icon}</span>
                {item.label}
                {item.badge && <span className="nav-badge">{item.badge}</span>}
              </NavLink>
            ))}
          </React.Fragment>
        ))}

        <div className="sidebar-footer">
          <div className="network-status">
            <div className="wallet-dot" style={{ width: 5, height: 5 }}></div>
            amitis-network · {blockHeight ? `#${blockHeight}` : 'live'}
          </div>
        </div>
      </aside>

      <WalletModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        nativeWallet={nativeWallet}
        onKeplrConnect={ctx.connectKeplr}
      />
    </>
  );
}
